/**
 * RssiWaveform: uPlot-backed RSSI time-series chart for the event permalink.
 *
 * Renders the RSSI waveform from alert.rssi_waveform with:
 *   - X axis: t_offset_ms / 1000 (seconds relative to alert fire at t=0)
 *   - Y axis: rssi_dbm, range -100 to -30 dBm (fixed scale)
 *   - Vertical markers: alert_fire (red/amber at x=0), turnaround (green) or
 *     entry (red) at x = (resolved_at - ts) in seconds when outcome is present
 *   - Time cursor: synced from CameraClip scrubbing via the timeCursor prop
 *
 * Phase 3: rssi_waveform is [] for all alerts (cloud does not yet mirror
 * per-detection RSSI). The component renders a placeholder message in that
 * case but still shows the vertical event markers on the (empty) axes.
 * Alert_fire and resolution markers are positioned by timestamp arithmetic.
 *
 * prefers-reduced-motion: uPlot does not animate by default; we disable
 * any CSS transition on the cursor line when reduced motion is active.
 *
 * JSDOM note: uPlot requires a DOM canvas. Unit tests mock uPlot via
 * vi.mock('uplot') and assert on the mock constructor args.
 */
import { useEffect, useRef } from 'react';
import uPlot from 'uplot';
import 'uplot/dist/uPlot.min.css';
import type { AlertDetail } from './schemas';
import * as styles from './RssiWaveform.css';
import { useReducedMotion } from '@/hooks/useReducedMotion';
import { theme } from '@/design/tokens.css';

/** Props for RssiWaveform. */
interface Props {
  /** Full alert detail providing rssi_waveform, ts, resolved_at, severity, outcome. */
  alert: AlertDetail;
  /** Current video time in seconds; null when no video is scrubbing. */
  timeCursor: number | null;
}

/**
 * RSSI time-series waveform with event markers.
 *
 * When rssi_waveform is empty (Phase 3 default), renders a placeholder.
 * Alert-fire and resolution markers are always shown regardless of waveform data.
 *
 * @param props - alert and timeCursor
 */
export function RssiWaveform({ alert, timeCursor }: Props) {
  const containerRef = useRef<HTMLDivElement>(null);
  const plotRef = useRef<uPlot | null>(null);
  const reducedMotion = useReducedMotion();

  const waveform = alert.rssi_waveform ?? [];
  const isEmpty = waveform.length === 0;

  useEffect(() => {
    if (!containerRef.current || isEmpty) return;

    // Destroy previous instance on re-render
    plotRef.current?.destroy();

    const xs = waveform.map((p) => p.t_offset_ms / 1000);
    const ys = waveform.map((p) => p.rssi_dbm);

    // Alert fire marker at t=0 (relative to ts)
    // Resolution marker at (resolved_at_epoch - ts_epoch) seconds
    const tsEpoch = new Date(alert.ts).getTime();
    const resolvedAt = alert.resolved_at ? new Date(alert.resolved_at).getTime() : null;
    const resolutionOffset = resolvedAt ? (resolvedAt - tsEpoch) / 1000 : null;

    // Color for alert-fire marker: status.warn (Tactical Gold) for warning,
    // status.alert (red) for critical/entry. Both route through theme tokens
    // so they swap automatically in light vs dark mode.
    const alertColor =
      alert.severity === 'critical' || alert.outcome === 'entered'
        ? theme.colors.status.alert
        : theme.colors.status.warn;

    const opts: uPlot.Options = {
      width: containerRef.current.clientWidth || 600,
      height: 160,
      scales: {
        x: { time: false },
        y: { auto: false, range: [-100, -30] },
      },
      axes: [
        {
          stroke: theme.colors.text.secondary,
          grid: { stroke: theme.colors.border.subtle },
        },
        {
          stroke: theme.colors.text.secondary,
          grid: { stroke: theme.colors.border.subtle },
          label: 'RSSI (dBm)',
          labelSize: 20,
        },
      ],
      series: [
        {},
        {
          stroke: theme.colors.accent.gold,
          width: 2,
        },
      ],
      hooks: {
        drawAxes: [
          (u: uPlot) => {
            const ctx = u.ctx;
            const { left, top, width, height: h } = u.bbox;

            // Alert-fire marker at t=0
            const x0 = u.valToPos(0, 'x', true);
            if (x0 >= left && x0 <= left + width) {
              ctx.save();
              ctx.strokeStyle = alertColor;
              ctx.lineWidth = 2;
              if (!reducedMotion) {
                ctx.setLineDash([4, 4]);
              }
              ctx.beginPath();
              ctx.moveTo(x0, top);
              ctx.lineTo(x0, top + h);
              ctx.stroke();
              ctx.restore();
            }

            // Resolution marker
            if (resolutionOffset !== null) {
              const xRes = u.valToPos(resolutionOffset, 'x', true);
              if (xRes >= left && xRes <= left + width) {
                ctx.save();
                // Resolution color: status.ok (green) for turned-away,
                // status.alert (red) for entered. Both route through tokens.
                ctx.strokeStyle =
                  alert.outcome === 'turned_away'
                    ? theme.colors.status.ok
                    : theme.colors.status.alert;
                ctx.lineWidth = 2;
                ctx.beginPath();
                ctx.moveTo(xRes, top);
                ctx.lineTo(xRes, top + h);
                ctx.stroke();
                ctx.restore();
              }
            }
          },
        ],
      },
    };

    // uPlot expects [xSeries, ySeries, ...]
    plotRef.current = new uPlot(opts, [xs, ys] as uPlot.AlignedData, containerRef.current);

    return () => {
      plotRef.current?.destroy();
      plotRef.current = null;
    };
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [alert.id, isEmpty, reducedMotion]);

  // Sync time cursor from video scrubbing
  useEffect(() => {
    if (!plotRef.current || timeCursor === null || isEmpty) return;
    try {
      const xPos = plotRef.current.valToPos(timeCursor, 'x');
      plotRef.current.setCursor({ left: xPos, top: 0 }, true);
    } catch {
      // Ignore: plot may be in a transient state during resize
    }
  }, [timeCursor, isEmpty]);

  if (isEmpty) {
    return (
      <div className={styles.placeholder} data-testid="rssi-waveform-placeholder">
        RSSI waveform unavailable for this event
      </div>
    );
  }

  return (
    <div className={styles.chartContainer} data-testid="rssi-waveform">
      <div ref={containerRef} className={styles.chart} />
    </div>
  );
}
